import axios from 'axios';
import { LABELS } from '@/i18n/labels';
import type { Lang } from '@/stores/langStore';

/**
 * Translates a thrown forgot-/reset-password error into a localized, user-facing message.
 * Mirrors admin-portal's `emailIdentityErrorMessage`: branch on the BACKEND ERROR CODE
 * (`response.data.error`), NOT the bare HTTP status. The reset-password endpoint returns a
 * distinct code for each 400 — `weak_password` (the token was fine; the new password fails the
 * house policy, which is stricter than this page's length-only pre-check) vs
 * `invalid_or_expired_token` (bad/used/expired link). Conflating them (the old status-only
 * mapping) told a user with a weak password that their LINK was invalid, sending them to
 * request a brand-new email instead of just choosing a stronger password (WS-5.A review W1/W2).
 *
 * Rules: NON-ENUMERATING — both codes are account-agnostic, so nothing here reveals whether an
 * account/email exists. No raw HTTP status codes, no raw `error.message`, no word "backend".
 * Any unrecognized non-2xx (including a 400 on the forgot page, which never issues a link)
 * falls through to a generic retry message rather than the link-specific copy.
 */
export function resetPasswordErrorMessage(error: unknown, lang: Lang = 'en'): string {
  const L = (key: keyof typeof LABELS) => LABELS[key][lang];

  if (axios.isAxiosError(error)) {
    // Network-layer failure — no HTTP response was received.
    if (!error.response) {
      return L('resetPwErrNetwork');
    }
    const code = (error.response.data as { error?: string } | undefined)?.error;
    if (code === 'weak_password') {
      return L('resetPwErrWeakPassword');
    }
    if (code === 'invalid_or_expired_token') {
      return L('resetPwErrInvalidLink');
    }
    if (error.response.status >= 500) {
      return L('resetPwErrServer');
    }
    return L('resetPwErrGeneric');
  }

  // Non-Axios throw — do NOT surface error.message (may leak internals).
  return L('resetPwErrGeneric');
}
